/**
 * Locale system for PluginAny Trip Planner.
 *
 * Single source of truth for:
 *   - Country detection (from user IP, profile, or manual selection)
 *   - Currency formatting (₹ for India, $ for US, etc.)
 *   - Distance units (km vs miles)
 *   - Temperature units (°C vs °F)
 *   - Speed units (km/h vs mph)
 *   - Pressure units (bar vs psi)
 *   - Energy stays universal (kWh, kW, Wh/km — global standards)
 *   - Date and time formatting
 *
 * Matches conventions established in the live PluginAny app (pluginany.com),
 * which uses a country selector at top of page driving all locale state.
 *
 * Design principle:
 *   - User's country is the SINGLE source of truth — no separate "units" preference
 *   - User can override country from settings (default: detected from IP)
 *   - All other prefs (currency, units, helplines, fuel-comparison) cascade from country
 *
 * Why: matches how the real world works — Indians think in km even if they
 * travel abroad; Americans think in miles even when in India. Keep it simple.
 */

const LOCALES = {
  IN: {
    country_code: 'IN',
    country_name: 'India',
    flag: '🇮🇳',
    currency_code: 'INR',
    currency_symbol: '₹',
    distance_unit: 'km',
    distance_factor_from_km: 1.0,
    temperature_unit: '°C',
    temperature_offset_from_c: 0,
    speed_unit: 'km/h',
    pressure_unit: 'bar',
    pressure_factor_from_kpa: 0.01,
    fuel_comparison_unit: '₹/litre',
    fuel_comparison_default_price: 105,
    date_locale: 'en-IN',
    number_locale: 'en-IN',
    timezone_default: 'Asia/Kolkata',
    emergency_number: '112',
    helplines_key: 'india',
    use_24h_time: true,
  },
  US: {
    country_code: 'US',
    country_name: 'United States',
    flag: '🇺🇸',
    currency_code: 'USD',
    currency_symbol: '$',
    distance_unit: 'mi',
    distance_factor_from_km: 0.621371,
    temperature_unit: '°F',
    temperature_offset_from_c: null,
    speed_unit: 'mph',
    pressure_unit: 'psi',
    pressure_factor_from_kpa: 0.145038,
    fuel_comparison_unit: '$/gallon',
    fuel_comparison_default_price: 3.50,
    date_locale: 'en-US',
    number_locale: 'en-US',
    timezone_default: 'America/Los_Angeles',
    emergency_number: '911',
    helplines_key: 'usa',
    use_24h_time: false,
  },
  GB: {
    country_code: 'GB',
    country_name: 'United Kingdom',
    flag: '🇬🇧',
    currency_code: 'GBP',
    currency_symbol: '£',
    distance_unit: 'mi',
    distance_factor_from_km: 0.621371,
    temperature_unit: '°C',
    temperature_offset_from_c: 0,
    speed_unit: 'mph',
    pressure_unit: 'bar',
    pressure_factor_from_kpa: 0.01,
    fuel_comparison_unit: '£/litre',
    fuel_comparison_default_price: 1.45,
    date_locale: 'en-GB',
    number_locale: 'en-GB',
    timezone_default: 'Europe/London',
    emergency_number: '999',
    helplines_key: 'uk',
    use_24h_time: true,
  },
  FR: {
    country_code: 'FR',
    country_name: 'France',
    flag: '🇫🇷',
    currency_code: 'EUR',
    currency_symbol: '€',
    distance_unit: 'km',
    distance_factor_from_km: 1.0,
    temperature_unit: '°C',
    temperature_offset_from_c: 0,
    speed_unit: 'km/h',
    pressure_unit: 'bar',
    pressure_factor_from_kpa: 0.01,
    fuel_comparison_unit: '€/litre',
    fuel_comparison_default_price: 1.85,
    date_locale: 'fr-FR',
    number_locale: 'fr-FR',
    timezone_default: 'Europe/Paris',
    emergency_number: '112',
    helplines_key: 'eu',
    use_24h_time: true,
  },
  DE: {
    country_code: 'DE',
    country_name: 'Germany',
    flag: '🇩🇪',
    currency_code: 'EUR',
    currency_symbol: '€',
    distance_unit: 'km',
    distance_factor_from_km: 1.0,
    temperature_unit: '°C',
    temperature_offset_from_c: 0,
    speed_unit: 'km/h',
    pressure_unit: 'bar',
    pressure_factor_from_kpa: 0.01,
    fuel_comparison_unit: '€/litre',
    fuel_comparison_default_price: 1.75,
    date_locale: 'de-DE',
    number_locale: 'de-DE',
    timezone_default: 'Europe/Berlin',
    emergency_number: '112',
    helplines_key: 'eu',
    use_24h_time: true,
  },
  CA: {
    country_code: 'CA',
    country_name: 'Canada',
    flag: '🇨🇦',
    currency_code: 'CAD',
    currency_symbol: 'C$',
    distance_unit: 'km',
    distance_factor_from_km: 1.0,
    temperature_unit: '°C',
    temperature_offset_from_c: 0,
    speed_unit: 'km/h',
    pressure_unit: 'kpa',
    pressure_factor_from_kpa: 1.0,
    fuel_comparison_unit: 'C$/litre',
    fuel_comparison_default_price: 1.65,
    date_locale: 'en-CA',
    number_locale: 'en-CA',
    timezone_default: 'America/Toronto',
    emergency_number: '911',
    helplines_key: 'canada',
    use_24h_time: false,
  },
  AU: {
    country_code: 'AU',
    country_name: 'Australia',
    flag: '🇦🇺',
    currency_code: 'AUD',
    currency_symbol: 'A$',
    distance_unit: 'km',
    distance_factor_from_km: 1.0,
    temperature_unit: '°C',
    temperature_offset_from_c: 0,
    speed_unit: 'km/h',
    pressure_unit: 'kpa',
    pressure_factor_from_kpa: 1.0,
    fuel_comparison_unit: 'A$/litre',
    fuel_comparison_default_price: 1.95,
    date_locale: 'en-AU',
    number_locale: 'en-AU',
    timezone_default: 'Australia/Sydney',
    emergency_number: '000',
    helplines_key: 'australia',
    use_24h_time: false,
  },
};

const FALLBACK_LOCALE = LOCALES.IN;

/**
 * Get the locale config for a country code.
 * Falls back to India if country is not supported.
 */
function getLocale(country_code) {
  if (!country_code) return FALLBACK_LOCALE;
  return LOCALES[country_code.toUpperCase()] || FALLBACK_LOCALE;
}

/**
 * Detect locale from various signals, in order of preference:
 *   1. Explicit user_country argument (from settings)
 *   2. Browser language hint
 *   3. Fallback to India
 *
 * In production, the country selector in the existing PluginAny app
 * provides explicit user_country — use that.
 */
function detectLocale({ user_country, browser_language } = {}) {
  if (user_country && LOCALES[user_country.toUpperCase()]) {
    return getLocale(user_country);
  }
  
  if (browser_language) {
    const region = browser_language.split('-')[1] || browser_language.split('_')[1];
    if (region && LOCALES[region.toUpperCase()]) {
      return getLocale(region);
    }
  }
  
  return FALLBACK_LOCALE;
}

function celsiusToFahrenheit(c) {
  return (c * 9/5) + 32;
}

function fahrenheitToCelsius(f) {
  return (f - 32) * 5/9;
}

function kmToMiles(km) {
  return km * 0.621371;
}

function milesToKm(mi) {
  return mi / 0.621371;
}

function kpaToBar(kpa) {
  return kpa * 0.01;
}

function kpaToPsi(kpa) {
  return kpa * 0.145038;
}

function barToKpa(bar) {
  return bar * 100;
}

function psiToKpa(psi) {
  return psi / 0.145038;
}

function formatDistance(km, locale, opts = {}) {
  const { precision = 0, showUnit = true } = opts;
  if (km == null || isNaN(km)) return '—';
  
  const value = km * locale.distance_factor_from_km;
  const rounded = precision === 0 ? Math.round(value) : value.toFixed(precision);
  const formatted = Number(rounded).toLocaleString(locale.number_locale);
  
  return showUnit ? `${formatted} ${locale.distance_unit}` : formatted;
}

function formatTemperature(c, locale, opts = {}) {
  const { showUnit = true } = opts;
  if (c == null || isNaN(c)) return '—';
  
  const value = locale.temperature_unit === '°F' ? celsiusToFahrenheit(c) : c;
  const rounded = Math.round(value);
  
  return showUnit ? `${rounded}${locale.temperature_unit}` : `${rounded}`;
}

function formatSpeed(kmh, locale, opts = {}) {
  const { showUnit = true } = opts;
  if (kmh == null || isNaN(kmh)) return '—';
  
  const value = locale.speed_unit === 'mph' ? kmh * 0.621371 : kmh;
  const rounded = Math.round(value);
  
  return showUnit ? `${rounded} ${locale.speed_unit}` : `${rounded}`;
}

function formatPressure(kpa, locale, opts = {}) {
  const { showUnit = true, precision = 1 } = opts;
  if (kpa == null || isNaN(kpa)) return '—';
  
  const value = kpa * locale.pressure_factor_from_kpa;
  const rounded = locale.pressure_unit === 'psi' 
    ? Math.round(value) 
    : value.toFixed(precision);
  
  return showUnit ? `${rounded} ${locale.pressure_unit}` : `${rounded}`;
}

function formatCurrency(amount, locale, opts = {}) {
  const { precision = 0, showSymbol = true, compact = false } = opts;
  if (amount == null || isNaN(amount)) return '—';
  
  if (compact && amount >= 1000) {
    if (amount >= 10000000 && locale.country_code === 'IN') {
      const cr = amount / 10000000;
      return `${locale.currency_symbol}${cr.toFixed(2)} Cr`;
    }
    if (amount >= 100000 && locale.country_code === 'IN') {
      const lakh = amount / 100000;
      return `${locale.currency_symbol}${lakh.toFixed(2)} L`;
    }
    if (amount >= 1000000) {
      return `${locale.currency_symbol}${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `${locale.currency_symbol}${(amount / 1000).toFixed(1)}K`;
    }
  }
  
  const formatted = new Intl.NumberFormat(locale.number_locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(amount);
  
  return showSymbol ? `${locale.currency_symbol}${formatted}` : formatted;
}

function formatPower(kw, opts = {}) {
  const { showUnit = true, precision = 0 } = opts;
  if (kw == null || isNaN(kw)) return '—';
  const rounded = precision === 0 ? Math.round(kw) : kw.toFixed(precision);
  return showUnit ? `${rounded} kW` : `${rounded}`;
}

function formatEnergy(kwh, opts = {}) {
  const { showUnit = true, precision = 1 } = opts;
  if (kwh == null || isNaN(kwh)) return '—';
  const rounded = precision === 0 ? Math.round(kwh) : kwh.toFixed(precision);
  return showUnit ? `${rounded} kWh` : `${rounded}`;
}

function formatConsumption(wh_km, locale, opts = {}) {
  const { showUnit = true } = opts;
  if (wh_km == null || isNaN(wh_km)) return '—';
  
  if (locale.distance_unit === 'mi') {
    const wh_mi = wh_km / 0.621371;
    const rounded = Math.round(wh_mi);
    return showUnit ? `${rounded} Wh/mi` : `${rounded}`;
  }
  
  const rounded = Math.round(wh_km);
  return showUnit ? `${rounded} Wh/km` : `${rounded}`;
}

function formatTime(minutes, opts = {}) {
  const { compact = false } = opts;
  if (minutes == null || isNaN(minutes)) return '—';
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return compact ? `${hours}h` : `${hours}h 0m`;
  return compact ? `${hours}h${mins}m` : `${hours}h ${mins}m`;
}

function formatClockTime(date, locale) {
  if (!(date instanceof Date)) date = new Date(date);
  if (isNaN(date.getTime())) return '—';
  
  return new Intl.DateTimeFormat(locale.date_locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: !locale.use_24h_time,
  }).format(date);
}

function formatDate(date, locale, opts = {}) {
  const { style = 'short' } = opts;
  if (!(date instanceof Date)) date = new Date(date);
  if (isNaN(date.getTime())) return '—';
  
  const optionsByStyle = {
    short: { day: 'numeric', month: 'short' },
    medium: { day: 'numeric', month: 'short', year: 'numeric' },
    long: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  };
  
  return new Intl.DateTimeFormat(locale.date_locale, optionsByStyle[style]).format(date);
}

function getEfficiencyComparisonNote(locale) {
  switch (locale.country_code) {
    case 'IN':
      return `Compare to petrol at ${locale.currency_symbol}${locale.fuel_comparison_default_price}/litre`;
    case 'US':
      return `Compare to gas at ${locale.currency_symbol}${locale.fuel_comparison_default_price.toFixed(2)}/gallon`;
    default:
      return `Compare to fuel at ${locale.fuel_comparison_unit}`;
  }
}

export {
  LOCALES,
  FALLBACK_LOCALE,
  getLocale,
  detectLocale,
  
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  kmToMiles,
  milesToKm,
  kpaToBar,
  kpaToPsi,
  barToKpa,
  psiToKpa,
  
  formatDistance,
  formatTemperature,
  formatSpeed,
  formatPressure,
  formatCurrency,
  formatPower,
  formatEnergy,
  formatConsumption,
  formatTime,
  formatClockTime,
  formatDate,
  
  getEfficiencyComparisonNote,
};
